<template>
  <div class="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
    <div class="w-full max-w-md space-y-10">
      <div>
        <img class="mx-auto h-30 w-auto" :src="logoUrl" alt="ManageRunners logo" />
        <h2
          class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white"
        >
          Join an organization
        </h2>
      </div>

      <div
        class="rounded-2xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
        data-testid="accept-invitation"
        :data-state="state"
      >
        <template v-if="state === 'ready' || state === 'accepting'">
          <div class="flex gap-3">
            <UserGroupIcon
              class="size-6 shrink-0 text-indigo-600 dark:text-indigo-400"
              aria-hidden="true"
            />
            <div class="text-sm text-gray-600 dark:text-gray-300">
              <p>You were invited to share runners in an organization on ManageRunners.</p>
              <p class="mt-2">
                Signed in as
                <span class="font-medium text-gray-900 dark:text-white">{{ auth.username }}</span
                >.
              </p>
            </div>
          </div>
          <div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <RouterLink
              :to="{ name: 'Dashboard' }"
              class="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20"
              >Not now</RouterLink
            >
            <button
              type="button"
              :disabled="state === 'accepting'"
              class="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-wait disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
              @click="accept"
            >
              {{ state === 'accepting' ? 'Joining…' : 'Accept invitation' }}
            </button>
          </div>
        </template>

        <div v-else-if="state === 'invalid'" role="alert" class="flex gap-3">
          <ExclamationTriangleIcon class="size-6 shrink-0 text-amber-500" aria-hidden="true" />
          <div class="text-sm">
            <p class="font-semibold text-gray-900 dark:text-white">This invitation can't be used</p>
            <p class="mt-1 text-gray-600 dark:text-gray-300">
              The link is invalid, has expired or was already used. Ask the organization owner to
              send a new invitation.
            </p>
            <RouterLink
              :to="{ name: 'Dashboard' }"
              class="mt-4 inline-block font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >Go to dashboard</RouterLink
            >
          </div>
        </div>

        <div v-else-if="state === 'mismatch'" role="alert" class="flex gap-3">
          <ExclamationTriangleIcon class="size-6 shrink-0 text-amber-500" aria-hidden="true" />
          <div class="text-sm">
            <p class="font-semibold text-gray-900 dark:text-white">
              Invitation is for another email address
            </p>
            <p class="mt-1 text-gray-600 dark:text-gray-300">
              You're signed in as
              <span class="font-medium text-gray-900 dark:text-white">{{ auth.username }}</span
              >. Sign in with the address the invitation was sent to, or create an account with it.
            </p>
            <button
              type="button"
              class="mt-4 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
              @click="switchAccount"
            >
              Sign in with another account
            </button>
          </div>
        </div>

        <div v-else role="alert" class="flex gap-3">
          <ExclamationTriangleIcon class="size-6 shrink-0 text-red-500" aria-hidden="true" />
          <div class="text-sm">
            <p class="font-semibold text-gray-900 dark:text-white">
              Could not accept the invitation
            </p>
            <p class="mt-1 text-gray-600 dark:text-gray-300">{{ errorMessage }}</p>
            <button
              type="button"
              class="mt-4 font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
              @click="state = 'ready'"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ExclamationTriangleIcon, UserGroupIcon } from '@heroicons/vue/24/outline';
import { acceptInvitation } from '@/api/organizations';
import { refreshOrganizations } from '@/lib/api';
import { organizationApiError, roleLabel } from '@/lib/organizations';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { useToastStore } from '@/stores/toast';

type State = 'ready' | 'accepting' | 'invalid' | 'mismatch' | 'error';

const logoUrl = new URL('../assets/logo_runner_small.png', import.meta.url).href;
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const organizations = useOrganizationStore();
const toast = useToastStore();

const token = computed(() => {
  const value = route.query.token;
  return (Array.isArray(value) ? value[0] : value)?.trim() ?? '';
});
const state = ref<State>(token.value ? 'ready' : 'invalid');
const errorMessage = ref('');
let left = false;
onBeforeUnmount(() => {
  left = true;
});

async function accept() {
  state.value = 'accepting';
  const session = auth.token;
  // After "Not now" or a change of account the result is left to the next membership load.
  const abandoned = () => left || auth.token !== session;
  try {
    const joined = await acceptInvitation(token.value);
    if (abandoned()) return;
    organizations.upsert(joined);
    organizations.select(joined.id);
    toast.success(`You joined “${joined.name}” as ${roleLabel(joined.role)}.`);
    await router.replace({ name: 'Dashboard', force: true });
    if (auth.token === session) void refreshOrganizations();
  } catch (e) {
    if (abandoned()) return;
    const error = organizationApiError(e, 'accept the invitation');
    if (error.code === 'INVITATION_INVALID') state.value = 'invalid';
    else if (error.code === 'INVITATION_EMAIL_MISMATCH') state.value = 'mismatch';
    else {
      errorMessage.value = error.message;
      state.value = 'error';
    }
  }
}

async function switchAccount() {
  auth.logout();
  await router.push({ name: 'Login', query: { redirect: route.fullPath } });
}
</script>
